import {
  useSaveRecordingDraftMutation,
  useUpdateRecordingMutation,
} from '@carboncopyco/data';
import type { IRecording } from '@carboncopyco/types';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useState } from 'react';
import ModalWrapper from '../components/Modals/ModalWrapper';
import StartRecording from '../components/StartRecording';
import { useCloneScreenRecording } from '../hooks/useCloneScreenRecording';
import { RecordingUpdateModal } from '../modals/RecordingUpdateModal';
import { useTabManager } from '../services/TabManager';
import { useTabs, useUser } from '../store/context';
import {
  startTrackingUserActivity,
  stopTrackingUserActivity,
} from './StartRecordingScreen';
import { useClonePreview } from './utils/useClonePreview';

export const UpdateRecordingPage = () => {
  const {
    getActiveGlobalTab,
    closeGlobalTab,
    closeGlobalActiveTab,
    createGlobalTab,
    updateGlobalTabPayload,
  } = useTabManager();
  const { activeOrganization } = useUser();
  const { activeGlobalTabId, activeRecordingData } = useTabs();
  const [isRecording, setIsRecording] = useState(false);
  const [isLoadingRecording, setIsLoadingRecording] = useState(false);
  const [isRecordingSavedModalOpen, setIsRecordingSavedModalOpen] =
    useState(false);
  const [updateRecording] = useUpdateRecordingMutation();
  const [saveRecordingDraft] = useSaveRecordingDraftMutation();
  const openClonePreview = useClonePreview();
  const { trackEvent } = useAnalytics();
  const { startScreenRecording, stopScreenRecording } =
    useCloneScreenRecording();

  const onSavedRecordingSubmitted = async (data: any) => {
    trackEvent(AnalyticsTrackEventName.CLONE_CLICK_FINISH_CLONING, {
      location: 'update_recording_page',
    });
    setIsLoadingRecording(true);
    await window.dsDesktop.recorder.destroyRecorder();
    await saveRecordingDraft({
      organizationId: `${activeOrganization.id}`,
      draftId: `${activeRecordingData?.id || ''}`,
      comment: data.description,
    }).unwrap();
    const updatedRecording = await updateRecording({
      organizationId: `${activeOrganization.id}`,
      id: getActiveGlobalTab().cloneId || '',
    }).unwrap();
    await stopScreenRecording(activeGlobalTabId, updatedRecording);
    setIsLoadingRecording(false);
    setIsRecording(false);
    setIsRecordingSavedModalOpen(true);
    stopTrackingUserActivity(activeRecordingData?.recorderUuid);
  };

  const onStartRecording = async () => {
    setIsLoadingRecording(true);
    trackEvent(AnalyticsTrackEventName.CLONE_CLICK_CONTINUE_CLONING, {
      location: 'update_recording_page',
    });
    updateGlobalTabPayload(getActiveGlobalTab()?.id, {
      cloningInProgress: true,
    });
    await window.dsDesktop.recorder.onStartUpdateRecording(
      getActiveGlobalTab()
    );
    setIsLoadingRecording(false);
    setIsRecording(true);
    if (activeRecordingData) {
      const adjustedRecording = { ...activeRecordingData };
      type IRecordingWithSourceId = IRecording & {
        sourceId: string;
      };
      adjustedRecording.id = (
        activeRecordingData as IRecordingWithSourceId
      ).sourceId;
      startTrackingUserActivity(adjustedRecording, activeOrganization.id);

      await startScreenRecording(activeGlobalTabId, activeRecordingData);
    }
  };

  const handlePreviewSavedRecording = () => {
    const globalTab = getActiveGlobalTab();
    closeGlobalActiveTab();
    window.dsDesktop.recorder.exitRecorder(globalTab);

    openClonePreview({
      cloneId: globalTab.cloneId || '',
      activeOrganization: { id: `${activeOrganization.id}` } || '',
      createGlobalTab,
    });
  };

  const onExitRecorder = () => {
    trackEvent(AnalyticsTrackEventName.CLONE_CLICK_DISCARD, {
      location: 'update_recording_page',
    });
    window.dsDesktop.recorder.exitRecorder(getActiveGlobalTab());
    closeGlobalTab(getActiveGlobalTab()?.id);
  };

  return (
    <div>
      <StartRecording
        updateRecording
        isRecording={isRecording}
        isLoading={isLoadingRecording}
        onStartRecording={onStartRecording}
        onSavedRecordingSubmitted={onSavedRecordingSubmitted}
        onExitRecorder={onExitRecorder}
      />

      <ModalWrapper
        isVisible={isRecordingSavedModalOpen}
        onCloseModal={() => setIsRecordingSavedModalOpen(false)}
        isCenterPosition
        removeCloseIcon
        locked
      >
        <RecordingUpdateModal
          onClose={onExitRecorder}
          previewSavedRecording={handlePreviewSavedRecording}
        />
      </ModalWrapper>
    </div>
  );
};
